import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { cn } from "@/lib/utils";

export interface SearchSelectOption {
  value: string;
  label: string;
}

/**
 * Typeahead for a paginated list endpoint. Fetches `search` + page_size=20 as the user types,
 * so a form does not depend on receiving every record.
 */
export function SearchSelect({
  value,
  onChange,
  endpoint,
  extraParams,
  getLabel,
  placeholder = "Search…",
  allowEmpty = false,
  emptyLabel = "None",
  selectedLabel,
  extraOptions = [],
  filterRow,
  disabled,
  className,
}: {
  value: string;
  onChange: (id: string, row: any | null) => void;
  endpoint: string;
  extraParams?: Record<string, string | undefined>;
  getLabel: (row: any) => string;
  placeholder?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
  selectedLabel?: string;
  extraOptions?: SearchSelectOption[];
  filterRow?: (row: any) => boolean;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const extraKey = JSON.stringify(extraParams || {});

  useEffect(() => {
    const onDoc = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      const qs = new URLSearchParams({ page: "1", page_size: "20" });
      if (text.trim()) qs.set("search", text.trim());
      for (const [key, param] of Object.entries(extraParams || {})) {
        if (param) qs.set(key, param);
      }
      const sep = endpoint.includes("?") ? "&" : "?";
      setLoading(true);
      api
        .get(`${endpoint}${sep}${qs.toString()}`)
        .then((res) => setRows(unwrapPage(res).rows))
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [open, text, endpoint, extraKey]);

  const shown = !open ? selectedLabel || (value ? value : "") : text;

  return (
    <div ref={boxRef} className={cn("relative", className)}>
      <input
        value={shown}
        disabled={disabled}
        placeholder={placeholder}
        onFocus={() => {
          setText("");
          setOpen(true);
        }}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        className="h-9 w-full rounded-md border border-input bg-background px-3 text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
      />
      {open && (
        <div
          ref={(el) => {
            // Inside a dialog the (scrolling, clipping) body would cut a floating list off, so the list takes its own room there
            if (el && boxRef.current?.closest('[role="dialog"]')) {
              el.style.position = "relative";
              el.scrollIntoView({ block: "nearest" });
            }
          }}
          className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
        >
          {allowEmpty && (
            <button
              type="button"
              className="block w-full px-3 py-2 text-left text-xs hover:bg-muted"
              onClick={() => {
                onChange("", null);
                setOpen(false);
              }}
            >
              {emptyLabel}
            </button>
          )}
          {extraOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className="block w-full px-3 py-2 text-left text-xs hover:bg-muted"
              onClick={() => {
                onChange(option.value, null);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
          {loading && (
            <div className="flex justify-center py-3">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {!loading && rows.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No matches</div>}
          {rows.filter((row) => (filterRow ? filterRow(row) : true)).map((row) => {
            const id = String(row.id);
            return (
              <button
                key={id}
                type="button"
                className={cn("block w-full px-3 py-2 text-left text-xs hover:bg-muted", id === value && "bg-muted")}
                onClick={() => {
                  onChange(id, row);
                  setOpen(false);
                }}
              >
                {getLabel(row)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
